/**
 * Source of truth: "Rbac clusters.xlsx" (Contract Cluster — RBAC Permission Matrix).
 * Maps every UI area — Cluster > Sub-Cluster/Area > Access Type — to the backend
 * permission code(s) that gate it. Keep this list in sync with that sheet.
 */
export interface PermissionClusterEntry {
  cluster: string;
  subCluster: string;
  accessType: string;
  action: string;
  permissionCode: string;
}

export const RBAC_PERMISSION_CLUSTERS: PermissionClusterEntry[] = [
  {
    cluster: 'Contract',
    subCluster: 'Contract Table',
    accessType: 'Read',
    action: 'View contract list',
    permissionCode: 'CONTRACT_TABLE_NATURAL_LANGUAGE_SEARCH_CONTRACTS',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Table',
    accessType: 'Read & Write',
    action: 'Upload Document',
    permissionCode: 'CONTRACT_TABLE_UPLOAD_DOCUMENT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Table',
    accessType: 'Read',
    action: 'View contract statistics',
    permissionCode: 'VIEW_CONTRACT_DASHBOARD_STATS',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'View contract details',
    permissionCode: 'CONTRACT_DETAILS_VIEW_CONTRACT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'View obligations',
    permissionCode: 'VIEW_CONTRACT_OBLIGATIONS',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'View audit trail',
    permissionCode: 'VIEW_CONTRACT_AUDIT_TRAIL',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'View metadata',
    permissionCode: 'VIEW_CONTRACT_METADATA',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Save all metadata',
    permissionCode: 'EDIT_CONTRACT_METADATA',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Edit obligation',
    permissionCode: 'CONTRACT_DETAILS_EDIT_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Set active document',
    permissionCode: 'ACTIVATE_CONTRACT_DOCUMENT_VERSION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Mark as resolved (reminder)',
    permissionCode: 'RESOLVE_REMINDER',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Mark contract as expired',
    permissionCode: 'EXPIRE_CONTRACT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read & Write',
    action: 'Add obligation',
    permissionCode: 'CONTRACT_DETAILS_CREATE_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Delete',
    action: 'Delete obligation',
    permissionCode: 'CONTRACT_DETAILS_DELETE_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'Download document',
    permissionCode: 'DOWNLOAD_CONTRACT_DOCUMENT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Details',
    accessType: 'Read',
    action: 'Document view',
    permissionCode: 'VIEW_CONTRACT_DOCUMENT_PREVIEW',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read',
    action: 'Contract Details',
    permissionCode: 'CONTRACT_REVIEW_VIEW_CONTRACT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read',
    action: 'Extract All',
    permissionCode: 'CONTRACT_REVIEW_EXTRACT_CONTRACT_ALL',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read & Write',
    action: 'Start Review',
    permissionCode: 'CONTRACT_REVIEW_START_CONTRACT_REVIEW',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read & Write',
    action: 'Obligation Edit',
    permissionCode: 'CONTRACT_REVIEW_EDIT_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read & Write',
    action: 'Save all metadata',
    permissionCode: 'CONTRACT_REVIEW_SAVE_ALL_METADATA',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read & Write',
    action: 'Save and Active Contract',
    permissionCode: 'CONTRACT_REVIEW_SAVE_AND_ACTIVATE_CONTRACT',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Read & Write',
    action: 'Create Obligation',
    permissionCode: 'CONTRACT_REVIEW_CREATE_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'Contract Review',
    accessType: 'Delete',
    action: 'Delete obligation',
    permissionCode: 'CONTRACT_REVIEW_DELETE_CONTRACT_OBLIGATION',
  },
  {
    cluster: 'Contract',
    subCluster: 'AI Processing',
    accessType: 'Read & Write',
    action: 'Extract All',
    permissionCode: 'AI_PROCESSING_EXTRACT_CONTRACT_ALL',
  },
  {
    cluster: 'Contract',
    subCluster: 'AI Processing',
    accessType: 'Read & Write',
    action: 'Start Review',
    permissionCode: 'AI_PROCESSING_START_CONTRACT_REVIEW',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Table',
    accessType: 'Read',
    action: 'Intake queue',
    permissionCode: 'VIEW_INTAKE_QUEUE',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Table',
    accessType: 'Read',
    action: 'Preview url',
    permissionCode: 'INTAKE_TABLE_VIEW_INTAKE_DOCUMENT_PREVIEW',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Table',
    accessType: 'Read & Write',
    action: 'Upload Document',
    permissionCode: 'INTAKE_TABLE_UPLOAD_DOCUMENT',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read',
    action: 'Intake Detail(particular intake)',
    permissionCode: 'VIEW_INTAKE_ITEM',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read',
    action: 'Preview url',
    permissionCode: 'INTAKE_DETAILS_VIEW_INTAKE_DOCUMENT_PREVIEW',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read',
    action: 'Ai Suggestions',
    permissionCode: 'VIEW_AI_SUGGESTIONS',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'Create new Contract',
    permissionCode: 'CREATE_CONTRACT_RECORD',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'Contract Search',
    permissionCode: 'SEARCH_CONTRACTS',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'Skip Document',
    permissionCode: 'SKIP_INTAKE_ITEM',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'Link to existing',
    permissionCode: 'LINK_EXISTING_CONTRACT',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'Check exact match',
    permissionCode: 'FIND_EXACT_MATCH',
  },
  {
    cluster: 'Intake',
    subCluster: 'Intake Details',
    accessType: 'Read & Write',
    action: 'AI processibility check',
    permissionCode: 'VIEW_DOCUMENT_PROCESSIBILITY',
  },
  {
    cluster: 'Dashboard',
    subCluster: 'Dashboard',
    accessType: 'Read',
    action: 'Active Contract By type',
    permissionCode: 'VIEW_ACTIVE_CONTRACTS_BY_TYPE',
  },
  {
    cluster: 'Dashboard',
    subCluster: 'Dashboard',
    accessType: 'Read',
    action: 'Dashboard Stats',
    permissionCode: 'VIEW_DASHBOARD_STATS',
  },
  {
    cluster: 'Dashboard',
    subCluster: 'Dashboard',
    accessType: 'Read',
    action: 'Recent activity',
    permissionCode: 'VIEW_RECENT_ACTIVITY',
  },
  {
    cluster: 'Dashboard',
    subCluster: 'Dashboard',
    accessType: 'Read',
    action: 'Contract List',
    permissionCode: 'DASHBOARD_VIEW_NATURAL_LANGUAGE_SEARCH_CONTRACTS',
  },
  {
    cluster: 'Reminders',
    subCluster: 'Reminders Table',
    accessType: 'Read',
    action: 'Reminders List',
    permissionCode: 'VIEW_REMINDERS_LIST',
  },
  {
    cluster: 'NLP',
    subCluster: 'Agentic Search Table',
    accessType: 'Read',
    action: 'Agentic Search',
    permissionCode: 'AGENTIC_SEARCH',
  },
  {
    cluster: 'Layout',
    subCluster: 'Application Layout',
    accessType: 'Read',
    action: 'Notifications',
    permissionCode: 'VIEW_NOTIFICATIONS',
  },
  {
    cluster: 'Layout',
    subCluster: 'Application Layout',
    accessType: 'Read',
    action: 'Reminder',
    permissionCode: 'VIEW_REMINDERS',
  },
  {
    cluster: 'Layout',
    subCluster: 'Application Layout',
    accessType: 'Read',
    action: 'Count',
    permissionCode: 'VIEW_DASHBOARD_COUNTS',
  },
  {
    cluster: 'Layout',
    subCluster: 'Application Layout',
    accessType: 'Read',
    action: 'Stream',
    permissionCode: 'STREAM_NOTIFICATIONS',
  },
  {
    cluster: 'Layout',
    subCluster: 'Application Layout',
    accessType: 'Read & Write',
    action: 'Notifications',
    permissionCode: 'DISMISS_NOTIFICATION',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read',
    action: 'View all Roles',
    permissionCode: 'VIEW_ROLES',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read',
    action: 'View all Users',
    permissionCode: 'VIEW_USERS',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read',
    action: 'View all Permissions',
    permissionCode: 'VIEW_PERMISSIONS',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read & Write',
    action: 'Add Role',
    permissionCode: 'CREATE_ROLE',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read & Write',
    action: 'Edit Role and its permissions',
    permissionCode: 'EDIT_ROLE',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Read & Write',
    action: 'Edit User data',
    permissionCode: 'EDIT_USER',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Delete',
    action: 'Delete role',
    permissionCode: 'DELETE_ROLE',
  },
  {
    cluster: 'Access Control',
    subCluster: 'Access Control',
    accessType: 'Delete',
    action: 'Delete User',
    permissionCode: 'DELETE_USER',
  },
];

export interface PermissionActionNode {
  action: string;
  permissionCode: string;
}

/** Keyed by Access Type (Read / Write / Delete). */
export type PermissionAccessTypeNode = Record<string, PermissionActionNode[]>;

/** Keyed by Sub-Cluster / Area. */
export type PermissionSubClusterNode = Record<string, PermissionAccessTypeNode>;

/** Keyed by Cluster. */
export type PermissionClusterTree = Record<string, PermissionSubClusterNode>;

/** Builds the full Cluster > Sub-Cluster > Access Type > Action tree, unfiltered. */
export function buildPermissionClusterTree(
  entries: PermissionClusterEntry[] = RBAC_PERMISSION_CLUSTERS
): PermissionClusterTree {
  const tree: PermissionClusterTree = {};

  for (const entry of entries) {
    const subClusters = (tree[entry.cluster] ??= {});
    const accessTypes = (subClusters[entry.subCluster] ??= {});
    const actions = (accessTypes[entry.accessType] ??= []);
    actions.push({ action: entry.action, permissionCode: entry.permissionCode });
  }

  return tree;
}

/**
 * Prunes the full cluster tree down to what `grantedPermissions` allows: an action
 * node survives only if its permission code is granted, and an Access Type,
 * Sub-Cluster, or Cluster branch is dropped entirely once it has no actions left.
 */
export function buildUserPermissionTree(
  grantedPermissions: readonly string[],
  entries: PermissionClusterEntry[] = RBAC_PERMISSION_CLUSTERS
): PermissionClusterTree {
  const granted = new Set(grantedPermissions);
  const fullTree = buildPermissionClusterTree(entries);
  const prunedTree: PermissionClusterTree = {};

  for (const [cluster, subClusters] of Object.entries(fullTree)) {
    const prunedSubClusters: PermissionSubClusterNode = {};

    for (const [subCluster, accessTypes] of Object.entries(subClusters)) {
      const prunedAccessTypes: PermissionAccessTypeNode = {};

      for (const [accessType, actions] of Object.entries(accessTypes)) {
        const prunedActions = actions.filter((actionNode) =>
          granted.has(actionNode.permissionCode)
        );
        if (prunedActions.length > 0) {
          prunedAccessTypes[accessType] = prunedActions;
        }
      }

      if (Object.keys(prunedAccessTypes).length > 0) {
        prunedSubClusters[subCluster] = prunedAccessTypes;
      }
    }

    if (Object.keys(prunedSubClusters).length > 0) {
      prunedTree[cluster] = prunedSubClusters;
    }
  }

  return prunedTree;
}

/**
 * True if the (pruned, per-user) tree grants Read access to `subCluster` under any
 * cluster — used to gate page routes and sidebar links by sub-cluster.
 */
export function hasSubClusterReadAccess(tree: PermissionClusterTree, subCluster: string): boolean {
  return hasSubClusterAccessType(tree, subCluster, 'Read');
}

/**
 * True if the (pruned, per-user) tree grants Read & Write access to `subCluster`
 * under any cluster — used to hide create/edit/action buttons the user isn't
 * allowed to use.
 */
export function hasSubClusterWriteAccess(tree: PermissionClusterTree, subCluster: string): boolean {
  return hasSubClusterAccessType(tree, subCluster, 'Read & Write');
}

/**
 * True if the (pruned, per-user) tree grants Delete access to `subCluster` under any
 * cluster — used to hide delete buttons the user isn't allowed to use.
 */
export function hasSubClusterDeleteAccess(
  tree: PermissionClusterTree,
  subCluster: string
): boolean {
  return hasSubClusterAccessType(tree, subCluster, 'Delete');
}

function hasSubClusterAccessType(
  tree: PermissionClusterTree,
  subCluster: string,
  accessType: string
): boolean {
  return Object.values(tree).some((subClusters) =>
    Boolean(subClusters[subCluster]?.[accessType]?.length)
  );
}

export interface PermissionClusterGroup {
  /** "<Sub-Cluster / Area> - <Access Type>", e.g. "Contract Details - Write". */
  label: string;
  subCluster: string;
  accessType: string;
  permissionCodes: string[];
}

/**
 * Groups the permission codes in `availableCodes` by (subCluster, accessType) per
 * `RBAC_PERMISSION_CLUSTERS`, labeling each group "<Sub-Cluster> - <Access Type>". A
 * cluster entry whose code isn't in `availableCodes` is skipped — shared by the role
 * checklist (`role-modal`) and the read-only role/permission matrix (`access-control`
 * page) so both group permissions identically.
 */
export function buildPermissionClusterGroups(
  availableCodes: Iterable<string>,
  entries: PermissionClusterEntry[] = RBAC_PERMISSION_CLUSTERS
): PermissionClusterGroup[] {
  const available = new Set(availableCodes);
  const groups = new Map<
    string,
    { label: string; subCluster: string; accessType: string; codes: Set<string> }
  >();

  for (const entry of entries) {
    if (!available.has(entry.permissionCode)) continue;

    const key = `${entry.subCluster}::${entry.accessType}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        label: `${entry.subCluster} - ${entry.accessType}`,
        subCluster: entry.subCluster,
        accessType: entry.accessType,
        codes: new Set(),
      };
      groups.set(key, group);
    }
    group.codes.add(entry.permissionCode);
  }

  return Array.from(groups.values()).map((group) => ({
    label: group.label,
    subCluster: group.subCluster,
    accessType: group.accessType,
    permissionCodes: Array.from(group.codes),
  }));
}
